import React, { useState, useEffect } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FaFileDownload, FaSearch, FaUserCircle } from "react-icons/fa";

const AllMember = () => {
  const [selectedCategory, setSelectedCategory] = useState("General"); // Updated fallback to match previous backend string standard
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetch(`https://mosque-backend-yfae.vercel.app/allMember`)
      .then((response) => response.json())
      .then((data) => {
        console.log("✅ Fetched all users:", data);
        setAllUsers(data);
      })
      .catch((error) => console.error("❌ Fetch error:", error));
  }, []);

  useEffect(() => {
    let filtered = allUsers.filter(
      (user) => user.category === selectedCategory,
    );

    if (searchTerm) {
      filtered = filtered.filter(
        (user) =>
          (user.name &&
            user.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (user.email &&
            user.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (user.phone && user.phone.includes(searchTerm)) ||
          (user.memberId &&
            user.memberId.toLowerCase().includes(searchTerm.toLowerCase())),
      );
    }

    setFilteredUsers(filtered);
  }, [selectedCategory, allUsers, searchTerm]);

  // Download PDF logic matching the previous layout pattern style
  const downloadTableAsPDF = () => {
    const doc = new jsPDF();
    autoTable(doc, {
      head: [["Member ID", "Full Name", "Email Address", "Phone Number"]],
      body: filteredUsers.map((user) => [
        user.memberId || "N/A",
        user.name || "N/A",
        user.email || "N/A",
        user.phone || "N/A",
      ]),
      styles: { fillColor: [254, 243, 199] },
      headStyles: { fillColor: [249, 115, 22] },
    });
    doc.save(`members-${selectedCategory.toLowerCase()}-log.pdf`);
  };

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-50 min-h-screen">
      {/* Top Controls Dashboard Header Area */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-6 border border-gray-100">
        <h1 className="text-3xl font-extrabold text-center text-gray-800 tracking-tight">
          Registered Membership Logs
        </h1>
        <p className="text-center text-gray-500 mt-1">
          Review authentic profiles configured on the {selectedCategory} ledger
          panel
        </p>

        {/* Category Filter Toggle Routing Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mt-6">
          {["General", "Premium"].map((category) => (
            <button
              key={category}
              className={`px-6 py-2.5 font-semibold text-sm rounded-lg transition-all duration-200 shadow-sm ${
                selectedCategory === category
                  ? "bg-orange-500 text-white scale-105 shadow-orange-100"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              onClick={() => setSelectedCategory(category)}
            >
              {category} Ledger
            </button>
          ))}
        </div>
      </div>

      {/* Modern Search Utility and Action Bar Controls Row */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <div className="relative flex-1 max-w-md">
          <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400">
            <FaSearch />
          </span>
          <input
            type="text"
            placeholder="Search by ID, name, email, or phone..."
            className="pl-10 pr-4 py-2 w-full bg-white border border-gray-300 rounded-lg text-sm shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <button
          onClick={downloadTableAsPDF}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium text-xs rounded-lg shadow-sm transition-all"
        >
          <FaFileDownload />
          Download Category Ledger (PDF)
        </button>
      </div>

      {/* Primary Management Data Log Grid Table Wrapper */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-gray-700 uppercase text-xs font-bold tracking-wider">
              <tr>
                <th className="p-4 text-left">Member ID</th>
                <th className="p-4 text-left">Full Name</th>
                <th className="p-4 text-left">Email Address</th>
                <th className="p-4 text-left">Phone Reference</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user, index) => (
                  <tr
                    key={index}
                    className="hover:bg-gray-50/70 transition-colors"
                  >
                    <td className="p-4 whitespace-nowrap font-mono text-xs font-bold text-gray-700">
                      {user.memberId || (
                        <span className="text-gray-400 italic font-sans font-normal">
                          Unassigned
                        </span>
                      )}
                    </td>
                    <td className="p-4 whitespace-nowrap font-semibold text-gray-900">
                      <div className="flex items-center gap-2">
                        <FaUserCircle className="text-gray-400 text-lg" />
                        <span>{user.name}</span>
                      </div>
                    </td>
                    <td className="p-4 whitespace-nowrap text-gray-600 font-medium">
                      {user.email}
                    </td>
                    <td className="p-4 whitespace-nowrap text-gray-500 font-mono text-xs select-all">
                      {user.phone || "N/A"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="4"
                    className="text-center py-12 text-gray-400 font-medium bg-gray-50/50 italic"
                  >
                    No active membership entries available matching this record
                    matrix
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AllMember;
